import {
  formatIsoDate,
  formatName,
  renderStatus,
} from "../../../utils/generalUtils";

import GeneratePdf from "../../../generatePdf/GeneratePdf";
import { Space } from "antd";
import ViewingFormButton from "../../Form/ViewingFormButton";

const SHORT_MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function formatAbsentDatesLabel(absentDates) {
  if (!Array.isArray(absentDates) || absentDates.length === 0) return "None";
  // New submissions store a single free-text string; old ones store ISO date strings.
  if (!/^\d{4}-\d{2}-\d{2}$/.test(absentDates[0])) {
    return absentDates[0] || "None";
  }
  return absentDates
    .map((ds) => {
      const d = new Date(ds + "T00:00:00");
      return `${SHORT_MONTHS[d.getMonth()]} ${d.getDate()}`;
    })
    .join(", ");
}

function formatAccomplishmentsLabel(accomplishments) {
  if (!Array.isArray(accomplishments) || accomplishments.length === 0) return "—";
  return (
    <ul style={{ margin: 0, paddingLeft: 20 }}>
      {accomplishments.map((a, i) => (
        <li key={i}>{a}</li>
      ))}
    </ul>
  );
}

export const cwaConfig = {
  docType: "Certificate of Work Accomplishment",
  title: "Certificate of Work Accomplishment",
  description:
    "Submit your Certificate of Work Accomplishment for the service period. List the tasks you completed under your contract of services. Your submission will be reviewed by your assigned approver. Once approved, you may download the signed certificate.",
  popupWidth: 900,
  apiConfig: {
    get: "/employee/cwa",
    post: "/employee/addcwa",
  },
  dataMapper: (item, index) => ({
    ...item,
    name: `${item?.submission?.submitter?.last_name || ""}, ${
      item?.submission?.submitter?.first_name || ""
    }`.trim(),
    formattedName: formatName(
      item?.submission?.submitter?.first_name,
      item?.submission?.submitter?.middle_name,
      item?.submission?.submitter?.last_name,
      "lfmcut"
    ),
    approver_name: `${item?.submission?.approval?.approver?.last_name || ""} ${
      item?.submission?.approval?.approver?.first_name || ""
    }`.trim(),
    formattedApproverName: formatName(
      item?.submission?.approval?.approver?.first_name,
      item?.submission?.approval?.approver?.middle_name,
      item?.submission?.approval?.approver?.last_name,
      "lfmcut"
    ),
    approver_position_name:
      item?.submission?.approval?.position?.position_name ?? "",
    approver_position_unit:
      item?.submission?.approval?.position?.unit ?? "",
    submitter_position: item?.submission?.position?.position_name,
    submitter_position_unit: item?.submission?.position?.unit,
    submitter_email: item?.submission?.submitter?.email,
    approval_date: item?.submission?.approval?.approval_date,
    remarks: item?.remarks,
    date_submitted: item?.created_at ? formatIsoDate(item?.created_at) : null,
    key: index + 1,
    status: item?.submission?.status,
  }),
  columns: (onDeleted) => [
    { title: "No.", dataIndex: "key", rowScope: "row", width: 60 },
    {
      title: "Position",
      dataIndex: "submitter_position",
      key: "submitter_position",
    },
    { title: "Start Date", dataIndex: "start_date", key: "start_date" },
    { title: "End Date", dataIndex: "end_date", key: "end_date" },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
      width: 160,
      ellipsis: true,
      render: (val) => val || "—",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: renderStatus(
        ["pending"],
        ["approved"],
        ["disapproved", "denied"]
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, item) => (
        <Space>
          <GeneratePdf
            docuType="cwa"
            contents={item}
            disabled={item?.status !== "approved"}
            isEmployee={true}
            isIcon={true}
          />
          <ViewingFormButton
            contents={item}
            viewingDataConfig={cwaConfig.viewingDataConfig}
            isIcon={true}
          />
        </Space>
      ),
    },
    {
      title: "Date Submitted",
      dataIndex: "date_submitted",
      key: "date_submitted",
    },
  ],
  submitTransform: (values) => ({
    ...values,
    absent_dates:
      typeof values.absent_dates === "string" && values.absent_dates.trim()
        ? [values.absent_dates]
        : [],
    remarks: values.remarks?.trim() || null,
  }),
  formFields: [
    {
      name: "date",
      label: "Period",
      type: "daterange",
      column: "left",
      rules: [{ required: true, message: "Please select a period", type: "array" }],
    },
    {
      name: "absent_dates",
      label: "Dates of Absence",
      type: "textarea",
      column: "left",
      placeholder: "e.g. Mar 3 (half day), Mar 10 (early out 4pm)",
      extra: "Indicate any absences, half days, or early outs during the period.",
      maxLength: 1000,
      showCount: true,
    },
    {
      name: "remarks",
      label: "Remarks",
      type: "textarea",
      column: "left",
      placeholder: "Optional remarks (max 30 words)",
      extra: "Brief remarks about this submission.",
      maxLength: 300,
      showCount: true,
    },
    {
      name: "accomplishments",
      label: "Accomplishments",
      type: "accomplishments-list",
      column: "right",
      rules: [
        {
          required: true,
          message: "Please add at least one accomplishment",
          validator: (_, value) => {
            if (
              Array.isArray(value) &&
              value.length > 0 &&
              value.every((v) => v && v.trim())
            ) {
              return Promise.resolve();
            }
            return Promise.reject(
              new Error("Please add at least one accomplishment")
            );
          },
        },
      ],
    },
  ],
  viewingDataConfig: [
    {
      key: 1,
      collapseName: "Period",
      collapseItems: [
        { key: 1, name: "start_date", label: "Start Date" },
        { key: 2, name: "end_date", label: "End Date" },
        {
          key: 3,
          name: "absent_dates",
          label: "Dates of Absence",
          render: (val) =>
            Array.isArray(val) ? formatAbsentDatesLabel(val) : val || "None",
        },
      ],
    },
    {
      key: 2,
      collapseName: "Accomplishments",
      collapseItems: [
        {
          key: 1,
          name: "accomplishments",
          label: "Accomplishments",
          render: (val) => formatAccomplishmentsLabel(val),
        },
      ],
    },
    {
      key: 3,
      collapseName: "Remarks",
      collapseItems: [
        { key: 1, name: "remarks", label: "Remarks", render: (val) => val || "None" },
      ],
    },
  ],
};
